import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { register, storeSession } from "../../shared/api/auth";
import { setPlatformAdminFlag } from "../../shared/api/platform";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type FieldErrors = Partial<
  Record<"clinicName" | "ownerEmail" | "ownerPassword" | "confirm", string>
>;

/** Public self-registration (auto-approved). Creates a clinic + owner and logs straight in. */
export function RegisterPage() {
  const navigate = useNavigate();
  const [clinicName, setClinicName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!clinicName.trim()) next.clinicName = "Vui lòng nhập tên phòng khám.";
    if (!ownerEmail.trim()) next.ownerEmail = "Vui lòng nhập email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail.trim()))
      next.ownerEmail = "Email không hợp lệ.";
    if (!ownerPassword) next.ownerPassword = "Vui lòng nhập mật khẩu.";
    else if (ownerPassword.length < 8)
      next.ownerPassword = "Mật khẩu tối thiểu 8 ký tự.";
    if (confirm !== ownerPassword) next.confirm = "Mật khẩu nhập lại không khớp.";
    return next;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setSubmitting(true);
    try {
      const trimmedEmail = ownerEmail.trim();
      const result = await register({
        clinicName: clinicName.trim(),
        ownerEmail: trimmedEmail,
        ownerPassword,
      });
      storeSession(result);
      setPlatformAdminFlag(false);
      // The owner is logged in immediately, but must still confirm the e-mail. Land on the
      // "check your inbox" thank-you page (it links into the dashboard) instead of the dashboard
      // directly, so the activation step is front-and-centre. An unverified session then shows the
      // waiting banner inside the app shell.
      navigate("/registered", { replace: true, state: { email: trimmedEmail } });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Đăng ký thất bại.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-3xl font-extrabold tracking-tight text-primary">
            <span aria-hidden="true">✚</span> Addy HIS
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Hệ thống quản lý phòng khám
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Đăng ký phòng khám</CardTitle>
            <CardDescription>
              Tạo phòng khám mới và tài khoản chủ phòng khám — dùng được ngay, chỉ cần
              xác nhận email sau đó.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="clinicName">Tên phòng khám</Label>
                <Input
                  id="clinicName"
                  type="text"
                  autoComplete="organization"
                  placeholder="VD: Phòng khám Đa khoa An Khang"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  aria-invalid={Boolean(errors.clinicName)}
                  disabled={submitting}
                />
                {errors.clinicName && (
                  <p className="text-sm text-destructive">{errors.clinicName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerEmail">Email chủ phòng khám</Label>
                <Input
                  id="ownerEmail"
                  type="email"
                  autoComplete="email"
                  placeholder="bacsi@phongkhamcuaban.vn"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  aria-invalid={Boolean(errors.ownerEmail)}
                  disabled={submitting}
                />
                {errors.ownerEmail && (
                  <p className="text-sm text-destructive">{errors.ownerEmail}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerPassword">Mật khẩu</Label>
                <div className="relative">
                  <Input
                    id="ownerPassword"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Tối thiểu 8 ký tự"
                    className="pr-14"
                    value={ownerPassword}
                    onChange={(e) => setOwnerPassword(e.target.value)}
                    aria-invalid={Boolean(errors.ownerPassword)}
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                    disabled={submitting}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? "Ẩn" : "Hiện"}
                  </button>
                </div>
                {errors.ownerPassword && (
                  <p className="text-sm text-destructive">{errors.ownerPassword}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">Nhập lại mật khẩu</Label>
                <Input
                  id="confirm"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Nhập lại mật khẩu"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  aria-invalid={Boolean(errors.confirm)}
                  disabled={submitting}
                />
                {errors.confirm && (
                  <p className="text-sm text-destructive">{errors.confirm}</p>
                )}
              </div>

              {formError && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {formError}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Đang tạo phòng khám…" : "Đăng ký & bắt đầu"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Đã có tài khoản?{" "}
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Đăng nhập
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
